import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {
  getLocalValue,
  LoacalVariables,
  updateMainHeader,
} from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { ORDER_COLORS as COLORS, orderFieldSx as fieldSx } from "../../../Components/Orders/OrderCardParts";
import {
  buildMatrix,
  countAvailable,
  countGranted,
  getPermissionModules,
  loadRoles,
  newRoleId,
  Operation,
  OPERATION_LABELS,
  OPERATIONS,
  PermissionMatrix,
  Role,
  saveRoles,
} from "./rolesStore";

const NAME_MAX = 50;
const DESCRIPTION_MAX = 200;
const NEW_ROLE = "__new__";

const checkboxSx = { color: COLORS.border, "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: COLORS.primary } };

interface Draft {
  name: string;
  description: string;
  permissions: PermissionMatrix;
}

type PendingConfirm = { kind: "discard"; proceed: () => void } | { kind: "delete" } | null;

// One page for both lab and pharmacy: roles on the left, the selected role's permissions on the right
export const RolesPermissions: React.FC = () => {
  const { showMessage } = useSnackbar();
  const vendorId: string = getLocalValue(LoacalVariables.UserId) || "";
  const isPharmacy = getLocalValue(LoacalVariables.UserType) === "pharmacy";
  const modules = useMemo(() => getPermissionModules(isPharmacy), [isPharmacy]);
  const available = countAvailable(modules);
  const emptyDraft = (): Draft => ({ name: "", description: "", permissions: buildMatrix(modules, () => false) });

  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [search, setSearch] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<PendingConfirm>(null);

  const isNew = selectedId === NEW_ROLE;
  const selectedRole = roles.find((r) => r.id === selectedId);
  const isSystem = !!selectedRole?.isSystem;

  const draftFromRole = (role?: Role): Draft =>
    role ? { name: role.name, description: role.description, permissions: role.permissions } : emptyDraft();

  useEffect(() => {
    updateMainHeader("Roles & Permissions");
    breadCrumbsStore.updateBreadCrumbs([{ Settings: "/settings/general" }, { "Roles & Permissions": "/settings/roles" }]);
  }, []);

  useEffect(() => {
    if (!vendorId) return;
    loadRoles(vendorId, isPharmacy).then((list) => {
      setRoles(list);
      setSelectedId(list[0]?.id || "");
      setDraft(draftFromRole(list[0]));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, isPharmacy]);

  const isDirty = useMemo(() => {
    const base = isNew ? emptyDraft() : draftFromRole(selectedRole);
    return JSON.stringify(base) !== JSON.stringify(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, selectedRole, isNew]);

  // Ask before throwing away unsaved edits
  const guard = (proceed: () => void) => (isDirty ? setPending({ kind: "discard", proceed }) : proceed());

  const selectRole = (id: string) =>
    guard(() => {
      setSelectedId(id);
      setDraft(id === NEW_ROLE ? emptyDraft() : draftFromRole(roles.find((r) => r.id === id)));
      setSubmitted(false);
    });

  const visibleRoles = roles.filter((r) =>
    `${r.name} ${r.description}`.toLowerCase().includes(search.trim().toLowerCase())
  );

  // --- permission rules: any action needs View; removing View removes everything else ---
  const setCell = (prev: PermissionMatrix, moduleKey: string, op: Operation, value: boolean): PermissionMatrix => {
    const ops = { ...prev[moduleKey] };
    if (op === "view" && !value) {
      Object.keys(ops).forEach((k) => (ops[k as Operation] = false));
    } else {
      ops[op] = value;
      if (value && op !== "view" && "view" in ops) ops.view = true;
    }
    return { ...prev, [moduleKey]: ops };
  };

  const updatePermissions = (fn: (prev: PermissionMatrix) => PermissionMatrix) =>
    setDraft((prev) => ({ ...prev, permissions: fn(prev.permissions) }));

  const toggleCell = (moduleKey: string, op: Operation, value: boolean) =>
    updatePermissions((prev) => setCell(prev, moduleKey, op, value));

  const toggleRow = (moduleKey: string, value: boolean) =>
    updatePermissions((prev) => {
      const module = modules.find((m) => m.key === moduleKey)!;
      return { ...prev, [moduleKey]: module.operations.reduce((ops, op) => ({ ...ops, [op]: value }), {}) };
    });

  const toggleColumn = (op: Operation, value: boolean) =>
    updatePermissions((prev) =>
      modules.filter((m) => m.operations.includes(op)).reduce((acc, m) => setCell(acc, m.key, op, value), prev)
    );

  const toggleAll = (value: boolean) => updatePermissions(() => buildMatrix(modules, () => value));

  const matrix = draft.permissions;
  const granted = countGranted(matrix);

  const rowState = (moduleKey: string) => {
    const values = Object.values(matrix[moduleKey] || {});
    const on = values.filter(Boolean).length;
    return { checked: values.length > 0 && on === values.length, indeterminate: on > 0 && on < values.length };
  };

  const columnState = (op: Operation) => {
    const applicable = modules.filter((m) => m.operations.includes(op));
    const on = applicable.filter((m) => matrix[m.key]?.[op]).length;
    return { checked: applicable.length > 0 && on === applicable.length, indeterminate: on > 0 && on < applicable.length };
  };

  const allState = { checked: granted === available, indeterminate: granted > 0 && granted < available };

  // --- validation ---
  const trimmedName = draft.name.trim();
  const nameError = !trimmedName
    ? "Role name is required"
    : roles.some((r) => r.id !== selectedId && r.name.trim().toLowerCase() === trimmedName.toLowerCase())
      ? "A role with this name already exists"
      : "";
  const permissionError = granted === 0 ? "Give this role at least one permission" : "";

  const handleSave = async () => {
    setSubmitted(true);
    if (isSystem || nameError || permissionError || !vendorId) return;
    setSaving(true);
    const role: Role = {
      id: isNew ? newRoleId(trimmedName) : selectedId,
      name: trimmedName,
      description: draft.description.trim(),
      permissions: draft.permissions,
      updatedAt: new Date().toISOString(),
    };
    const next = isNew ? [...roles, role] : roles.map((r) => (r.id === role.id ? role : r));
    try {
      await saveRoles(vendorId, next);
      setRoles(next);
      setSelectedId(role.id);
      setDraft(draftFromRole(role));
      setSubmitted(false);
      showMessage(isNew ? `Role "${role.name}" created` : `Role "${role.name}" saved`, "success");
    } catch (e) {
      console.error("Failed to save role:", e);
      showMessage("Failed to save role", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole || isSystem) return;
    const next = roles.filter((r) => r.id !== selectedRole.id);
    try {
      await saveRoles(vendorId, next);
      setRoles(next);
      setSelectedId(next[0]?.id || "");
      setDraft(draftFromRole(next[0]));
      showMessage(`Role "${selectedRole.name}" deleted`, "success");
    } catch (e) {
      console.error("Failed to delete role:", e);
      showMessage("Failed to delete role", "error");
    }
  };

  const confirmPending = () => {
    const current = pending;
    setPending(null);
    if (current?.kind === "discard") current.proceed();
    if (current?.kind === "delete") handleDelete();
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ color: COLORS.muted, fontSize: "0.875rem", mb: 2, px: 0.5 }}>
        Create roles for your {isPharmacy ? "pharmacy" : "lab"} staff and choose what each role can view, add, edit,
        delete or export.
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "300px 1fr" }, gap: 2, alignItems: "start" }}>
        {/* Roles */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", border: `1px solid ${COLORS.border}` }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography component="h2" sx={{ fontWeight: 700, color: COLORS.dark }}>
              Roles <Box component="span" sx={{ color: COLORS.muted, fontWeight: 500 }}>{roles.length}</Box>
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => selectRole(NEW_ROLE)}
              sx={{ color: COLORS.primary, textTransform: "none", fontWeight: 600 }}
            >
              New role
            </Button>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by role name or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ "aria-label": "Search by role name or description" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: COLORS.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...fieldSx, mb: 1.5 }}
          />
          <Box role="listbox" aria-label="Roles" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {isNew && (
              <Box sx={{ p: 1.5, borderRadius: "12px", border: `1px solid ${COLORS.primary}`, bgcolor: COLORS.light }}>
                <Typography sx={{ fontWeight: 600, color: COLORS.dark, fontSize: "0.9rem" }}>
                  {trimmedName || "New role"}
                </Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem" }}>Not saved yet</Typography>
              </Box>
            )}
            {visibleRoles.map((role, index) => {
              const selected = role.id === selectedId;
              return (
                <ButtonBase
                  key={role.id}
                  role="option"
                  aria-selected={selected}
                  onClick={() => selectRole(role.id)}
                  sx={{
                    display: "block",
                    textAlign: "left",
                    p: 1.5,
                    borderRadius: "12px",
                    border: `1px solid ${selected ? COLORS.primary : COLORS.border}`,
                    bgcolor: selected ? COLORS.light : "#fff",
                    "&:hover": { borderColor: COLORS.primary },
                    "&.Mui-focusVisible": { outline: `2px solid ${COLORS.primary}`, outlineOffset: 2 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: COLORS.dark, fontSize: "0.9rem" }} noWrap>
                      <Box component="span" sx={{ color: COLORS.muted, fontWeight: 500, mr: 0.75 }}>{index + 1}.</Box>
                      {role.name}
                    </Typography>
                    {role.isSystem && (
                      <Chip size="small" label="Built-in" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#F1F2F2", color: "#55595A" }} />
                    )}
                  </Box>
                  <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem", mt: 0.25 }}>
                    {role.isSystem ? "Full access" : `${countGranted(role.permissions)} of ${available} permissions`}
                  </Typography>
                </ButtonBase>
              );
            })}
            {visibleRoles.length === 0 && (
              <Typography sx={{ color: COLORS.muted, fontSize: "0.85rem", textAlign: "center", py: 2 }}>
                No roles match your search.
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Selected role: details + permissions */}
        {(selectedRole || isNew) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            {isSystem && (
              <Alert severity="info" sx={{ borderRadius: "12px" }}>
                Administrator is a built-in role with full access. It can't be changed or deleted.
              </Alert>
            )}

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "16px", border: `1px solid ${COLORS.border}` }}>
              <Typography component="h2" sx={{ fontWeight: 700, color: COLORS.dark, mb: 2 }}>
                {isNew ? "New role" : "Role details"}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" }, gap: 2 }}>
                <TextField
                  size="small"
                  label="Role name *"
                  value={draft.name}
                  disabled={isSystem}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value.slice(0, NAME_MAX) }))}
                  error={submitted && !!nameError}
                  helperText={submitted && nameError ? nameError : `${draft.name.length}/${NAME_MAX}`}
                  sx={fieldSx}
                />
                <TextField
                  size="small"
                  label="Description"
                  value={draft.description}
                  disabled={isSystem}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value.slice(0, DESCRIPTION_MAX) }))}
                  helperText={`${draft.description.length}/${DESCRIPTION_MAX}`}
                  sx={fieldSx}
                />
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "16px", border: `1px solid ${COLORS.border}` }}>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <Typography component="h2" sx={{ fontWeight: 700, color: COLORS.dark }}>Permissions</Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: "0.85rem" }}>
                  {granted} of {available} selected
                </Typography>
              </Box>
              <Typography sx={{ color: COLORS.muted, fontSize: "0.8rem", mb: 1.5 }}>
                Add, Edit, Delete and Export need View, so it's switched on with them. "—" means the action doesn't apply to that page.
              </Typography>
              {submitted && permissionError && (
                <Typography sx={{ color: "#D32F2F", fontSize: "0.8rem", mb: 1 }}>{permissionError}</Typography>
              )}

              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 620 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Checkbox
                            size="small"
                            disabled={isSystem}
                            checked={allState.checked}
                            indeterminate={allState.indeterminate}
                            onChange={(e) => toggleAll(e.target.checked)}
                            inputProps={{ "aria-label": "Select all permissions" }}
                            sx={checkboxSx}
                          />
                          Page
                        </Box>
                      </TableCell>
                      {OPERATIONS.map((op) => {
                        const state = columnState(op);
                        return (
                          <TableCell key={op} align="center" sx={{ fontWeight: 700, fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25 }}>
                              <Checkbox
                                size="small"
                                disabled={isSystem}
                                checked={state.checked}
                                indeterminate={state.indeterminate}
                                onChange={(e) => toggleColumn(op, e.target.checked)}
                                inputProps={{ "aria-label": `${OPERATION_LABELS[op]} on all pages` }}
                                sx={checkboxSx}
                              />
                              {OPERATION_LABELS[op]}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modules.map((module) => {
                      const state = rowState(module.key);
                      return (
                        <TableRow key={module.key} hover>
                          <TableCell sx={{ fontSize: "0.85rem", color: COLORS.dark }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Checkbox
                                size="small"
                                disabled={isSystem}
                                checked={state.checked}
                                indeterminate={state.indeterminate}
                                onChange={(e) => toggleRow(module.key, e.target.checked)}
                                inputProps={{ "aria-label": `All permissions for ${module.label}` }}
                                sx={checkboxSx}
                              />
                              {module.label}
                            </Box>
                          </TableCell>
                          {OPERATIONS.map((op) => (
                            <TableCell key={op} align="center">
                              {module.operations.includes(op) ? (
                                <Checkbox
                                  size="small"
                                  disabled={isSystem}
                                  checked={!!matrix[module.key]?.[op]}
                                  onChange={(e) => toggleCell(module.key, op, e.target.checked)}
                                  inputProps={{ "aria-label": `${OPERATION_LABELS[op]} ${module.label}` }}
                                  sx={checkboxSx}
                                />
                              ) : (
                                <Typography component="span" sx={{ color: COLORS.border }} aria-label="Not applicable">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {!isSystem && (
              <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, pb: 2 }}>
                <Box>
                  {!isNew && (
                    <Button color="error" onClick={() => setPending({ kind: "delete" })} sx={{ textTransform: "none", fontWeight: 600 }}>
                      Delete role
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Button
                    disabled={!isDirty && !isNew}
                    onClick={() => (isNew ? selectRole(roles[0]?.id || "") : setDraft(draftFromRole(selectedRole)))}
                    sx={{ color: COLORS.muted, textTransform: "none", fontWeight: 600 }}
                  >
                    {isNew ? "Cancel" : "Discard changes"}
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    disabled={saving || (!isDirty && !isNew)}
                    onClick={handleSave}
                    sx={{ bgcolor: COLORS.primary, borderRadius: "10px", textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: COLORS.dark } }}
                  >
                    {saving ? "Saving..." : isNew ? "Create role" : "Save changes"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Confirm: discard unsaved edits / delete role */}
      <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700 }}>
          {pending?.kind === "delete" ? "Delete role?" : "Discard unsaved changes?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.875rem" }}>
            {pending?.kind === "delete"
              ? `"${selectedRole?.name}" and its permissions will be removed. This can't be undone.`
              : "Your changes to this role haven't been saved."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPending(null)} sx={{ textTransform: "none", color: COLORS.muted }}>
            {pending?.kind === "delete" ? "Cancel" : "Keep editing"}
          </Button>
          <Button onClick={confirmPending} color="error" variant="contained" disableElevation sx={{ textTransform: "none", borderRadius: "10px" }}>
            {pending?.kind === "delete" ? "Delete" : "Discard"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPermissions;
